// Noun Project API integration for vocabulary icons

// Environment variables are now handled by the backend API


export interface NounProjectIcon {
  id: string;
  term: string;
  permalink: string;
  preview_url: string;
  preview_url_42: string;
  preview_url_84: string;
}

export interface NounProjectResponse {
  icons: NounProjectIcon[];
}

// Cache for API responses to avoid repeated calls
const iconCache = new Map<string, string | null>();

/**
 * Fetches an icon from the Noun Project API for a given vocabulary term
 * @param term The vocabulary term to search for (now expects English meaning)
 * @returns Promise<string | null> - The icon URL or null if not found
 */
export async function getNounProjectIcon(term: string): Promise<string | null> {
  // Check cache first
  if (iconCache.has(term)) {
    return iconCache.get(term) || null;
  }

  try {
    // Use our backend API endpoint to avoid CORS issues
    const response = await fetch(`/api/noun-project-icon?term=${encodeURIComponent(term)}`);
    
    if (!response.ok) {
      console.warn(`[NounProject] API error for "${term}": ${response.status}`);
      iconCache.set(term, null);
      return null;
    }

    const data = await response.json();
    
    if (data.iconUrl) {
      iconCache.set(term, data.iconUrl);
      return data.iconUrl;
    }
    
    iconCache.set(term, null);
    return null;
  } catch (error) {
    console.warn(`[NounProject] Error fetching icon for "${term}":`, error);
    iconCache.set(term, null);
    return null;
  }
}

/**
 * Preloads icons for a list of vocabulary terms
 * @param terms Array of vocabulary terms
 */
export async function preloadNounProjectIcons(terms: string[]): Promise<void> {
  const promises = terms.map(term => getNounProjectIcon(term));
  await Promise.allSettled(promises);
}

/**
 * Clears the icon cache (useful for testing or when credentials change)
 */
export function clearNounProjectCache(): void {
  iconCache.clear();
}
